import { useEffect, useReducer, type Dispatch, type Reducer } from "react";
import type { ComponentSpec, MetricOption, SubjectEntity } from "../../Processing/types";

interface ConfiguratorSyncOptions<TState, TAction, TSpec extends ComponentSpec | null> {
  reducer: Reducer<TState, TAction>;
  existingSpec: NonNullable<TSpec> | undefined;
  hydrate: (spec: NonNullable<TSpec>) => TState;
  createInitial: () => TState;
  initialState?: TState;
  toSpec: (
    state: TState,
    metrics: MetricOption[],
    getSubjectEntities?: (t: string) => SubjectEntity[],
  ) => TSpec;
  onSpecChange: (spec: TSpec) => void;
  metrics: MetricOption[];
  getSubjectEntities: (t: string) => SubjectEntity[];
}

/**
 * Shared hook that wires up the useReducer + useEffect(onSpecChange) pattern
 * used by all configurator components (Plot, Table, and future widget types).
 */
export function useConfiguratorSync<TState, TAction, TSpec extends ComponentSpec | null>({
  reducer,
  existingSpec,
  hydrate,
  createInitial,
  initialState,
  toSpec,
  onSpecChange,
  metrics,
  getSubjectEntities,
}: ConfiguratorSyncOptions<TState, TAction, TSpec>): [TState, Dispatch<TAction>] {
  const [state, dispatch] = useReducer(
    reducer,
    existingSpec,
    (existing) => (existing ? hydrate(existing) : initialState ?? createInitial()),
  );

  useEffect(() => {
    onSpecChange(toSpec(state, metrics, getSubjectEntities));
  }, [state, metrics, onSpecChange, getSubjectEntities, toSpec]);

  return [state, dispatch];
}
